import { getSource } from "@/lib/refSource";
import { metaBrowserIds, metaLeadFrom } from "@/lib/meta";
import { getSessionId } from "@/advisor/api";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, MapPin, Loader2, CheckCircle2, Gift } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, errMsg } from "@/lib/api";
import { PhoneInput } from "./PhoneInput";
import { Socials, WhatsAppIcon, waLink } from "./Socials";
import { useWebPrice, Manat } from "./PriceBadge";

export const LEAD_SERVICES = ["Veb sayt", "Veb tətbiq", "Mobil tətbiq", "Dizayn", "Loqo", "Telegram bot", "TikTok canlı yayım oyunu", "Digər"];

const IMG = "/images/contact.jpg";
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function ContactInfo({ c }) {
  const rows = [
    c.phone && { Icon: Phone, v: c.phone, href: `tel:${c.phone.replace(/\s/g, "")}` },
    c.email && { Icon: Mail, v: c.email, href: `mailto:${c.email}` },
    c.address && { Icon: MapPin, v: c.address },
    (c.whatsapp || c.phone) && { Icon: WhatsAppIcon, v: c.whatsapp || c.phone, href: waLink(c.whatsapp || c.phone), ext: true },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <ul className="mt-8 space-y-3 text-sm" data-testid="contact-info">
      {rows.map(({ Icon, v, href, ext }, i) => (
        <li key={i} className="flex items-center gap-3"><Icon size={16} className="text-[#b58a3f]" />{href ? <a href={href} className="hover:underline" data-testid={ext ? "contact-whatsapp-link" : undefined} aria-label={ext ? `WhatsApp: ${v}` : undefined} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{v}</a> : v}</li>
      ))}
    </ul>
  );
}

export default function ContactSection() {
  const { config } = useSite();
  const price = useWebPrice();
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [reqId, setReqId] = useState(newId);
  const [calc, setCalc] = useState(null);
  useEffect(() => {
    const onPrefill = (e) => {
      const { calc: q, calcSession, ...rest } = e.detail;
      setForm((f) => ({ ...f, ...rest }));
      setCalc(q ? { q, calcSession } : null);
    };
    window.addEventListener("lm:prefill", onPrefill);
    return () => window.removeEventListener("lm:prefill", onPrefill);
  }, []);
  const [state, setState] = useState({ loading: false, error: "", done: null });

  const [loyal, setLoyal] = useState(0);
  useEffect(() => {
    setLoyal(0);
    if (form.phone.length !== 9 || !config.contacts?.loyalty_enabled) return;
    const t = setTimeout(() => api.post("/loyalty/check", { phone: `+994${form.phone}` }).then((r) => setLoyal(r.data.percent || 0)).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [form.phone, config.contacts?.loyalty_enabled]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setState((s) => ({ ...s, error: "Adınızı daxil edin" }));
    if (form.phone.length !== 9) return setState((s) => ({ ...s, error: "Telefon nömrəsini tam daxil edin (+994 XX XXX XX XX)" }));
    if (!form.service) return setState((s) => ({ ...s, error: "Xidmət kateqoriyasını seçin" }));
    setState({ loading: true, error: "", done: null });
    try {
      const { data } = await api.post("/leads/contact", { ref_source: getSource(), name: form.name, phone: `+994${form.phone}`, service: form.service, message: form.message, request_id: reqId, calc: form.service === "Veb sayt" ? calc?.q : null, calc_session: calc?.calcSession || "", visitor_id: getSessionId(), meta: metaBrowserIds() });
      if (!data.duplicate) metaLeadFrom(data.meta_event);
      setState({ loading: false, error: "", done: data.lead.code });
      setForm({ name: "", phone: "", service: "", message: "" });
      setReqId(newId());
      setCalc(null);
    } catch (err) {
      setState({ loading: false, error: errMsg(err), done: null });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-20 scroll-mt-16">
      <div className="container-lm grid lg:grid-cols-[1fr_1.35fr_0.9fr] gap-10 items-center">
        <div>
          <p className="eyebrow">Əlaqə</p>
          <h2 className="font-display text-3xl sm:text-[34px] font-semibold mt-4 leading-tight">İndi öz ideyanızı reallaşdırın</h2>
          <p className="text-sm text-[#77736B] mt-4">Layihəniz barədə danışmaq üçün bizimlə əlaqə saxlayın.</p>
          <a href="#contact-form" className="btn-dark mt-7" data-testid="contact-cta-btn">Əlaqə saxlayın <ArrowRight size={16} className="arr" /></a>
          <ContactInfo c={config.contacts} />
          <Socials contacts={config.contacts} className="mt-6" />
        </div>
        <form id="contact-form" onSubmit={submit} noValidate data-testid="contact-form" className="bg-white/80 rounded-2xl p-5 sm:p-6 shadow-[0_20px_50px_-30px_rgba(23,23,21,.35)] border border-[#171715]/5">
          {state.done ? (
            <div className="py-8 text-center" data-testid="contact-success" role="status">
              <CheckCircle2 className="mx-auto text-[#b58a3f]" size={36} />
              <p className="font-display font-semibold text-lg mt-4">Müraciətiniz uğurla göndərildi!</p>
              <p className="text-sm text-[#77736B] mt-1">Sizinlə əlaqə saxlayacağıq. Müraciət ID: <b>{state.done}</b></p>
              <button type="button" onClick={() => setState({ loading: false, error: "", done: null })} className="btn-ghost mt-5" data-testid="contact-new-btn">Yeni müraciət</button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="sr-only" htmlFor="c-name">Ad və soyad</label>
                <input id="c-name" data-testid="contact-name-input" className="lm-input" placeholder="Ad və soyad *" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} autoComplete="name" />
                <label className="sr-only" htmlFor="c-phone">Telefon nömrəsi</label>
                <PhoneInput id="c-phone" testid="contact-phone-input" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
              </div>
              {loyal > 0 && (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-[#EEF4F1] border border-[#2F5D50]/20 px-4 py-3 text-sm text-[#1f4238] loyal-in" data-testid="contact-loyalty-banner" role="status">
                  <Gift size={18} className="shrink-0 text-[#2F5D50]" />
                  <span><b>Yenidən xoş gördük!</b> Daimi müştəri kimi bu sifarişə <b>{loyal}% endirim</b> tətbiq olunacaq.</span>
                </div>
              )}
              <label className="sr-only" htmlFor="c-service">Xidmət kateqoriyası</label>
              <select id="c-service" data-testid="contact-service-select" className={`lm-input mt-3 ${form.service ? "" : "text-[#77736B]"}`} value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}>
                <option value="" disabled>Xidmət kateqoriyası *</option>
                {LEAD_SERVICES.map((s) => <option key={s} value={s} className="text-[#171715]">{s}</option>)}
              </select>
              {form.service === "Veb sayt" && price && (
                <p className="text-xs text-[#77736B] mt-2" data-testid="contact-web-price-hint">Veb saytların qiyməti <b className="text-[#171715]"><Manat text={price} /></b>-dan başlayır.</p>
              )}
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 mt-3 items-end">
                <label className="sr-only" htmlFor="c-msg">Layihə haqqında qısa məlumat</label>
                <textarea id="c-msg" data-testid="contact-message-input" className="lm-input min-h-[88px] resize-none" placeholder="Layihəniz haqqında qısa məlumat"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={1500} />
                <button type="submit" disabled={state.loading} className="btn-dark" data-testid="contact-submit-btn">
                  {state.loading ? <Loader2 size={16} className="animate-spin" /> : <>Göndər <ArrowRight size={16} className="arr" /></>}
                </button>
              </div>
              {state.error && <p className="text-sm text-red-700 mt-3" role="alert" data-testid="contact-error">{state.error}</p>}
              <p className="text-[11px] text-[#77736B] mt-3">
                Göndərməklə <Link to="/mexfilik" className="underline">məxfilik siyasəti</Link> ilə razılaşırsınız.
              </p>
            </>
          )}
        </form>
        <div className="hidden lg:block rounded-2xl overflow-hidden aspect-[4/3.4]">
          <img src={config.contacts.contact_image || IMG} alt="livemedia.az studiya" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}
